import type { EdgeType } from './EdgeType';
import { Edge } from './EdgeType';
import type {
  Busbar,
  ConnectivityNode,
  EnergyConsumer,
  ExternalNetworkInjection,
  FunctionalGrid,
  FunctionalGridEdge,
  FunctionalGridElement,
  FunctionalGridElementTypes,
  PowerTransformer
} from './FunctionalGrid';
import type { VerticeType } from './VerticeType';
import { Vertice } from './VerticeType';

export class FunctionalGridGraph {
  private vertices: VerticeType[];
  private edges: EdgeType[];

  constructor(functionalGrid: FunctionalGrid) {
    this.vertices = this.createVertices(functionalGrid);
    this.edges = this.createEdges(functionalGrid);
  }

  private createVertices(functionalGrid: FunctionalGrid): VerticeType[] {
    let vertices: VerticeType[] = [];

    vertices = vertices.concat(
      functionalGrid.busbars.map(busbar => Vertice.fromBusbar(busbar))
    );
    vertices = vertices.concat(
      functionalGrid.energyConsumers.map(energyConsumer =>
        Vertice.fromEnergyConsumer(energyConsumer)
      )
    );
    vertices = vertices.concat(
      functionalGrid.externalNetworkInjections.map(
        externalNetworkInjection =>
          Vertice.fromExternalNetworkInjection(externalNetworkInjection)
      )
    );
    vertices = vertices.concat(
      functionalGrid.powerTransformers.map(powerTransformer =>
        Vertice.fromPowerTransformer(powerTransformer)
      )
    );
    vertices.concat(
      functionalGrid.connectivityNodes.map(connectivityNode =>
        Vertice.fromConnectivityNode(connectivityNode)
      )
    );

    return vertices;
  }

  private createEdges(functionalGrid: FunctionalGrid): EdgeType[] {
    let edges: EdgeType[] = [];

    edges = edges.concat(
      functionalGrid.acLineSegments.map(acLineSegment =>
        Edge.fromAcLineSegment(acLineSegment)
      )
    );
    edges = edges.concat(
      functionalGrid.powerTransformerEnds.map(powerTransformerEnd =>
        Edge.fromPowerTransformerEnd(powerTransformerEnd)
      )
    );
    edges = edges.concat(
      functionalGrid.terminals.map(terminal => Edge.fromTerminal(terminal))
    );
    edges = edges.concat(
      functionalGrid.switches.map(switchEdge => Edge.fromSwitch(switchEdge))
    );

    return edges;
  }

  public deepFirstSearchFromEdge(
    startEdge: FunctionalGridEdge,
    targetType: FunctionalGridElementTypes,
    visitedAtStartType: FunctionalGridElementTypes
  ): FunctionalGridElement | null {
    let stack: VerticeType[] = [];

    let visited: FunctionalGridElement[] = [];

    visited = visited.concat(
      this.vertices.filter(vertex => vertex.type === visitedAtStartType)
    );
    const adjacentVertices = this.getAdjacentVerticesForEdge(startEdge);
    stack = stack.concat(adjacentVertices);

    while (stack.length > 0) {
      const currentVertex = stack.pop();
      if (currentVertex && !visited.includes(currentVertex)) {
        visited = visited.concat(currentVertex);

        if (currentVertex.type === targetType) {
          return currentVertex;
        }

        const adjacentVertices = this.getAdjacentVertices(currentVertex);
        stack = stack.concat(adjacentVertices);
      }
    }

    return null;
  }

  private getAdjacentVerticesForEdge(edge: FunctionalGridEdge): VerticeType[] {
    let adjacentVertices: VerticeType[] = [];

    const adjacentVertex2 = this.vertices.find(
      v => v.objectId === edge.connectivity2
    );
    if (adjacentVertex2) {
      adjacentVertices = adjacentVertices.concat(adjacentVertex2);
    }
    const adjacentVertex1 = this.vertices.find(
      v => v.objectId === edge.connectivity1
    );
    if (adjacentVertex1) {
      adjacentVertices = adjacentVertices.concat(adjacentVertex1);
    }

    return adjacentVertices;
  }

  private getAdjacentVertices(
    vertex:
      | PowerTransformer
      | Busbar
      | ConnectivityNode
      | EnergyConsumer
      | ExternalNetworkInjection
  ): VerticeType[] {
    let adjacentVertices: VerticeType[] = [];

    this.edges.forEach(edge => {
      if (edge.connectivity1 === vertex.objectId) {
        const adjacentVertex = this.vertices.find(
          v => v.objectId === edge.connectivity2
        );
        if (adjacentVertex) {
          adjacentVertices.concat(adjacentVertex);
        }
      } else if (edge.connectivity2 === vertex.objectId) {
        const adjacentVertex = this.vertices.find(
          v => v.objectId === edge.connectivity1
        );
        if (adjacentVertex) {
          adjacentVertices.concat(adjacentVertex);
        }
      }
    });

    return adjacentVertices;
  }
}
